import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import GoalsFilters from '../../components/Goals/GoalsFilters';
import GoalForm from '../../components/Goals/GoalForm';
import GoalDetails from '../../components/Goals/GoalDetails';
import { 
  getTargetTypeLabel, 
  getStatusLabel, 
  getPeriodLabel,
  getStatusColor,
  getProgressColor
} from '../../constants/goalsConstants';

const GoalsPage = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const [goals, setGoals] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Stan filtrów
  const [filters, setFilters] = useState({
    year: 'all',
    month: 'all',
    quarter: 'all',
    targetType: 'all',
    assignedTo: 'all',
    status: 'all',
    onlyCompanyGoals: false,
    onlyIndividualGoals: false
  });

  // Pobierz dane przy pierwszym renderowaniu
  useEffect(() => {
    fetchGoals();
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin, currentUser]);

  // Filtruj cele przy zmianie filtrów
  useEffect(() => {
    applyFilters();
  }, [goals, filters]);

  // Funkcja pobierająca cele
  const fetchGoals = async () => {
    try {
      setLoading(true);
      let goalsQuery;
      
      if (isAdmin) {
        // Administrator widzi wszystkie cele
        goalsQuery = query(
          collection(db, 'goals'),
          orderBy('year', 'desc'),
          orderBy('month', 'desc')
        );
      } else {
        // Zwykły pracownik widzi tylko swoje cele i cele firmowe
        goalsQuery = query(
          collection(db, 'goals'),
          where('assignedTo', 'in', [currentUser.uid, null])
        );
      }
      
      const goalsSnapshot = await getDocs(goalsQuery);
      const goalsList = await Promise.all(goalsSnapshot.docs.map(async doc => {
        const goalData = doc.data();
        
        // Pobierz dane o przypisanym pracowniku (jeśli istnieje)
        let assignedToName = '';
        if (goalData.assignedTo) {
          try {
            const userRef = doc(db, 'users', goalData.assignedTo);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              assignedToName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
            }
          } catch (error) {
            console.error(`Błąd podczas pobierania danych pracownika:`, error);
          }
        }
        
        return {
          id: doc.id,
          ...goalData,
          startDate: goalData.startDate?.toDate() || new Date(),
          endDate: goalData.endDate?.toDate() || new Date(),
          createdAt: goalData.createdAt?.toDate() || new Date(),
          lastUpdate: goalData.lastUpdate?.toDate() || new Date(),
          assignedToName
        };
      }));
      
      setGoals(goalsList);
      setLoading(false);
    } catch (error) {
      console.error('Błąd podczas pobierania celów:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas pobierania celów',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Funkcja pobierająca pracowników
  const fetchEmployees = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const employeesList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        firstName: doc.data().firstName || '',
        lastName: doc.data().lastName || '',
        email: doc.data().email || '',
        role: doc.data().role || 'user'
      }));
      setEmployees(employeesList);
    } catch (error) {
      console.error('Błąd podczas pobierania pracowników:', error);
      setEmployees([]);
    }
  };

  // Funkcja filtrująca cele
  const applyFilters = () => {
    let filtered = [...goals];
    
    // Filtrowanie po roku
    if (filters.year !== 'all') {
      filtered = filtered.filter(goal => goal.year === parseInt(filters.year));
    }
    
    // Filtrowanie po miesiącu
    if (filters.month !== 'all') {
      filtered = filtered.filter(goal => goal.month === parseInt(filters.month));
    }
    
    // Filtrowanie po kwartale
    if (filters.quarter !== 'all') {
      filtered = filtered.filter(goal => goal.quarter === parseInt(filters.quarter));
    }
    
    // Filtrowanie po typie celu
    if (filters.targetType !== 'all') {
      filtered = filtered.filter(goal => goal.targetType === filters.targetType);
    }
    
    // Filtrowanie po statusie
    if (filters.status !== 'all') {
      filtered = filtered.filter(goal => goal.status === filters.status);
    }
    
    // Filtrowanie po przypisanym pracowniku
    if (filters.assignedTo !== 'all') {
      if (filters.assignedTo === 'company') {
        filtered = filtered.filter(goal => !goal.assignedTo);
      } else {
        filtered = filtered.filter(goal => goal.assignedTo === filters.assignedTo);
      }
    }
    
    // Filtrowanie po typie celu (firmowy/indywidualny)
    if (filters.onlyCompanyGoals) {
      filtered = filtered.filter(goal => !goal.assignedTo);
    }
    
    if (filters.onlyIndividualGoals) {
      filtered = filtered.filter(goal => !!goal.assignedTo);
    }
    
    // Sortowanie po roku i miesiącu (malejąco)
    filtered.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      
      // Jeśli cele są miesięczne
      if (a.month && b.month) return b.month - a.month;
      
      // Jeśli cele są kwartalne
      if (a.quarter && b.quarter) return b.quarter - a.quarter;
      
      // Jeśli jeden cel jest miesięczny, a drugi kwartalny lub roczny
      if (a.month && !b.month) return -1;
      if (!a.month && b.month) return 1;
      
      // Jeśli jeden cel jest kwartalny, a drugi roczny
      if (a.quarter && !b.quarter) return -1;
      if (!a.quarter && b.quarter) return 1;
      
      return 0;
    });
    
    setFilteredGoals(filtered);
    // Reset paginacji przy zmianie filtrów
    setPage(0);
  };

  // Obsługa zmiany filtrów
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Obsługa paginacji
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Obsługa dodawania/edycji celu
  const handleSaveGoal = (goalData) => {
    // Odśwież listę celów
    fetchGoals();
    
    // Zamknij dialog
    setFormDialogOpen(false);
    
    setSnackbar({
      open: true,
      message: selectedGoal ? 'Cel został zaktualizowany' : 'Nowy cel został dodany',
      severity: 'success'
    });
    
    // Wyczyść wybrany cel
    setSelectedGoal(null);
  };

  // Obsługa usuwania celu
  const handleDeleteGoal = async () => {
    if (!selectedGoal) return;
    
    try {
      await deleteDoc(doc(db, 'goals', selectedGoal.id));
      
      // Odśwież listę celów
      fetchGoals();
      
      // Zamknij dialog
      setDeleteDialogOpen(false);
      setDetailsDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: 'Cel został usunięty',
        severity: 'success'
      });
      
      // Wyczyść wybrany cel
      setSelectedGoal(null);
    } catch (error) {
      console.error('Błąd podczas usuwania celu:', error);
      setSnackbar({
        open: true,
        message: 'Wystąpił błąd podczas usuwania celu',
        severity: 'error'
      });
    }
  };

  // Format daty
  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'dd.MM.yyyy', { locale: pl });
  };

  // Obliczanie procentu realizacji
  const calculateProgress = (current, target) => {
    if (target <= 0) return 0;
    return Math.min(Math.round((current / target) * 100), 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Nagłówek */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Cele Sprzedażowe</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedGoal(null);
              setFormDialogOpen(true);
            }}
          >
            Dodaj cel
          </Button>
        )}
      </Box>

      {/* Filtry */}
      <GoalsFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        employees={employees}
        isAdmin={isAdmin}
      />

      {/* Tabela celów */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Okres</TableCell>
                <TableCell>Typ celu</TableCell>
                <TableCell>Przypisane do</TableCell>
                <TableCell>Wartość docelowa</TableCell>
                <TableCell>Aktualny postęp</TableCell>
                <TableCell>Realizacja</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredGoals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Brak celów spełniających kryteria
                  </TableCell>
                </TableRow>
              ) : (
                // Wyświetl przefiltrowane i spaginowane cele
                filteredGoals
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((goal) => {
                    const progressPercentage = calculateProgress(goal.currentProgress, goal.targetValue);
                    
                    return (
                      <TableRow key={goal.id} hover>
                        {/* Okres */}
                        <TableCell>
                          {getPeriodLabel(goal.year, goal.month, goal.quarter)}
                        </TableCell>
                        
                        {/* Typ celu */}
                        <TableCell>{getTargetTypeLabel(goal.targetType)}</TableCell>
                        
                        {/* Przypisane do */}
                        <TableCell>
                          {goal.assignedTo ? goal.assignedToName || goal.assignedTo : 'Cała firma'}
                        </TableCell>
                        
                        {/* Wartość docelowa */}
                        <TableCell>
                          {goal.targetValue} {goal.targetType === 'sales_value' ? 'PLN' : ''}
                        </TableCell>
                        
                        {/* Aktualny postęp */}
                        <TableCell>
                          {goal.currentProgress} {goal.targetType === 'sales_value' ? 'PLN' : ''}
                        </TableCell>
                        
                        {/* Realizacja */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={progressPercentage} 
                                color={getProgressColor(goal.currentProgress, goal.targetValue)}
                                sx={{ height: 8, borderRadius: 5 }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                              <Typography variant="body2" color="textSecondary">{`${progressPercentage}%`}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        {/* Status */}
                        <TableCell>
                          <Chip 
                            label={getStatusLabel(goal.status)} 
                            color={getStatusColor(goal.status)}
                            size="small"
                          />
                        </TableCell>
                        
                        {/* Akcje */}
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {/* Przycisk szczegółów */}
                            <IconButton
                              color="primary"
                              onClick={() => {
                                setSelectedGoal(goal);
                                setDetailsDialogOpen(true);
                              }}
                              size="small"
                              title="Szczegóły"
                            >
                              <ViewIcon />
                            </IconButton>
                            
                            {/* Przycisk edycji (tylko dla admina) */}
                            {isAdmin && (
                              <IconButton
                                color="secondary"
                                onClick={() => {
                                  setSelectedGoal(goal);
                                  setFormDialogOpen(true);
                                }}
                                size="small"
                                title="Edytuj"
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            
                            {/* Przycisk usunięcia (tylko dla admina) */}
                            {isAdmin && (
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setSelectedGoal(goal);
                                  setDeleteDialogOpen(true);
                                }}
                                size="small"
                                title="Usuń"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Paginacja */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredGoals.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
        />
      </Paper>

      {/* Dialog dodawania/edycji celu */}
      <Dialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedGoal(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedGoal ? 'Edytuj cel' : 'Dodaj nowy cel'}
        </DialogTitle>
        <DialogContent>
          <GoalForm
            initialData={selectedGoal}
            onSubmit={handleSaveGoal}
            onCancel={() => {
              setFormDialogOpen(false);
              setSelectedGoal(null);
            }}
            employees={employees}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog szczegółów celu */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedGoal(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {selectedGoal && (
            <GoalDetails
              goal={selectedGoal}
              onEdit={() => {
                setDetailsDialogOpen(false);
                setFormDialogOpen(true);
              }}
              onDelete={() => {
                setDeleteDialogOpen(true);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog potwierdzenia usunięcia */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <Typography>
            Czy na pewno chcesz usunąć ten cel? Ta operacja jest nieodwracalna.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            color="inherit"
          >
            Anuluj
          </Button>
          <Button 
            onClick={handleDeleteGoal} 
            color="error"
            variant="contained"
          >
            Usuń
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar do powiadomień */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GoalsPage;
